import * as THREE from 'three';
import { Enemy } from './Enemy';
import { EnemyType } from './EnemyType';

/**
 * Brute enemy - stronger melee attacker with more health
 */
export class Brute extends Enemy {
  private attackHitbox: THREE.Mesh;
  private attackAnimationProgress: number = 0;
  private weapon: THREE.Group = new THREE.Group(); // Initialize with empty group
  
  /**
   * Creates a new Brute enemy
   * @param position The initial position
   * @param scene The scene to add the enemy to
   */
  constructor(position: THREE.Vector3, scene: THREE.Scene) {
    super(EnemyType.BRUTE, position, scene);
    
    // Create attack hitbox (invisible)
    const hitboxGeometry = new THREE.BoxGeometry(2.5, 1.5, 2.5);
    const hitboxMaterial = new THREE.MeshBasicMaterial({ 
      color: 0xff0000,
      transparent: true,
      opacity: 0.0, // Invisible
      depthWrite: false
    });
    this.attackHitbox = new THREE.Mesh(hitboxGeometry, hitboxMaterial);
    this.attackHitbox.position.z = 1.5; // In front of the enemy
    this.attackHitbox.visible = false;
    this.object.add(this.attackHitbox);
    
    // Add brute-specific details to the model
    this.addModelDetails();
  }
  
  /**
   * Adds visual details to the brute model
   */
  private addModelDetails(): void {
    // Add eyes
    const eyeGeometry = new THREE.SphereGeometry(0.12, 8, 8);
    const eyeMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000 }); // Red eyes
    
    const leftEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
    leftEye.position.set(0.25, 0.3, 0.5);
    this.model.add(leftEye);
    
    const rightEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
    rightEye.position.set(-0.25, 0.3, 0.5);
    this.model.add(rightEye);
    
    // Add horns
    const hornGeometry = new THREE.ConeGeometry(0.1, 0.3, 8);
    const hornMaterial = new THREE.MeshStandardMaterial({ color: 0x333333 });
    
    const leftHorn = new THREE.Mesh(hornGeometry, hornMaterial);
    leftHorn.position.set(0.4, 0.6, 0.2);
    leftHorn.rotation.x = -Math.PI / 4;
    leftHorn.rotation.z = -Math.PI / 6;
    this.model.add(leftHorn);
    
    const rightHorn = new THREE.Mesh(hornGeometry, hornMaterial);
    rightHorn.position.set(-0.4, 0.6, 0.2);
    rightHorn.rotation.x = -Math.PI / 4;
    rightHorn.rotation.z = Math.PI / 6;
    this.model.add(rightHorn);
    
    // Add armor plates
    const addArmorPlate = (x: number, y: number, z: number, width: number, height: number, depth: number) => {
      const plateGeometry = new THREE.BoxGeometry(width, height, depth);
      const plateMaterial = new THREE.MeshStandardMaterial({ color: 0x444444, metalness: 0.8, roughness: 0.2 });
      const plate = new THREE.Mesh(plateGeometry, plateMaterial);
      plate.position.set(x, y, z);
      this.model.add(plate);
    };
    
    // Chest plate
    addArmorPlate(0, 0.2, 0.45, 0.8, 0.8, 0.1);
    
    // Shoulder plates
    addArmorPlate(0.5, 0.5, 0, 0.3, 0.2, 0.6);
    addArmorPlate(-0.5, 0.5, 0, 0.3, 0.2, 0.6);
    
    // Create weapon (hammer)
    this.weapon = this.createWeapon();
    this.weapon.position.set(0.6, 0.2, 0.2);
    this.model.add(this.weapon);
  }
  
  /**
   * Creates the brute's weapon
   * @returns The weapon group
   */
  private createWeapon(): THREE.Group {
    const weaponGroup = new THREE.Group();
    
    // Handle
    const handleGeometry = new THREE.CylinderGeometry(0.05, 0.05, 1.2, 8);
    const handleMaterial = new THREE.MeshStandardMaterial({ color: 0x8B4513 });
    const handle = new THREE.Mesh(handleGeometry, handleMaterial);
    weaponGroup.add(handle);
    
    // Hammer head
    const headGeometry = new THREE.BoxGeometry(0.3, 0.3, 0.5);
    const headMaterial = new THREE.MeshStandardMaterial({ color: 0x888888, metalness: 0.8, roughness: 0.2 });
    const head = new THREE.Mesh(headGeometry, headMaterial);
    head.position.y = 0.7;
    weaponGroup.add(head);
    
    // Spikes on hammer
    const spikeGeometry = new THREE.ConeGeometry(0.05, 0.15, 4);
    const spikeMaterial = new THREE.MeshStandardMaterial({ color: 0x666666 });
    
    const addSpike = (x: number, y: number, z: number, rotationX: number, rotationZ: number) => {
      const spike = new THREE.Mesh(spikeGeometry, spikeMaterial);
      spike.position.set(x, y, z);
      spike.rotation.x = rotationX;
      spike.rotation.z = rotationZ;
      head.add(spike);
    };
    
    // Add spikes to hammer head
    addSpike(0, 0.15, 0.25, Math.PI / 2, 0);
    addSpike(0, 0.15, -0.25, -Math.PI / 2, 0);
    addSpike(0.15, 0.15, 0, 0, Math.PI / 2);
    addSpike(-0.15, 0.15, 0, 0, -Math.PI / 2);
    
    return weaponGroup;
  }
  
  /**
   * Makes the brute attack
   */
  public attack(): void {
    if (this.isDead || !this.isActive) return;
    
    const currentTime = performance.now() / 1000;
    if (currentTime - this.lastAttackTime < this.attackCooldown) {
      return; // Still on cooldown
    }
    
    super.attack();
    
    // Show attack hitbox
    this.attackHitbox.visible = true;
    
    // Reset attack animation progress
    this.attackAnimationProgress = 0;
  }
  
  /**
   * Updates the brute
   * @param deltaTime Time since last update in seconds
   * @param playerPosition The current player position
   */
  public update(deltaTime: number, playerPosition: THREE.Vector3): void {
    super.update(deltaTime, playerPosition);
    
    // Update attack animation
    if (this.isAttacking) {
      this.updateAttackAnimation(deltaTime);
    }
  }
  
  /**
   * Updates the attack animation
   * @param deltaTime Time since last update in seconds
   */
  private updateAttackAnimation(deltaTime: number): void {
    // Progress the attack animation
    this.attackAnimationProgress += deltaTime;
    
    // Normalize progress to 0-1 range
    const normalizedProgress = Math.min(this.attackAnimationProgress / 1.5, 1.0);
    
    // Animate the weapon for attack
    if (normalizedProgress < 0.4) {
      // Wind up phase - raise hammer
      const windupProgress = normalizedProgress / 0.4; // 0 to 1
      this.weapon.rotation.x = -windupProgress * Math.PI / 2; // Raise up
    } else if (normalizedProgress < 0.7) {
      // Strike phase - bring hammer down
      const strikeProgress = (normalizedProgress - 0.4) / 0.3; // 0 to 1
      this.weapon.rotation.x = -Math.PI / 2 + strikeProgress * Math.PI; // Swing down
      
      // Check for hit at the peak of the strike
      if (strikeProgress > 0.8 && strikeProgress < 0.9) {
        this.checkAttackHit();
      }
    } else {
      // Recovery phase - return to normal
      const recoveryProgress = (normalizedProgress - 0.7) / 0.3; // 0 to 1
      this.weapon.rotation.x = Math.PI / 2 - recoveryProgress * Math.PI / 2; // Return to normal
    }
    
    // Reset weapon rotation when attack is complete
    if (normalizedProgress >= 1.0) {
      this.weapon.rotation.x = 0;
      this.attackHitbox.visible = false;
    }
  }
  
  /**
   * Checks if the attack hits the player
   */
  private checkAttackHit(): void {
    // This would normally use collision detection with the player
    // For now, we'll just log that an attack occurred
    console.log('Brute attack!');
    
    // In a real implementation, this would check for collision with the player
    // and apply damage if a hit is detected
  }
  
  /**
   * Handles brute death
   */
  protected die(): void {
    super.die();
    
    // Play death animation - fall over
    this.model.rotation.x = Math.PI / 2; // Fall forward
    this.model.position.z = this.getHeight() / 2; // Move forward as it falls
    this.model.position.y = 0; // Lie on the ground
  }
  
  /**
   * Disposes of resources
   */
  public dispose(): void {
    super.dispose();
    
    // Dispose of additional resources
    if (this.attackHitbox.geometry) {
      this.attackHitbox.geometry.dispose();
    }
    
    if (this.attackHitbox.material instanceof THREE.Material) {
      this.attackHitbox.material.dispose();
    }
    
    // Dispose of weapon
    this.weapon.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        if (child.geometry) {
          child.geometry.dispose();
        }
        
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
    });
  }
} 